import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  Stack,
  Tooltip,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupIcon from "@mui/icons-material/Group";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import SecurityIcon from "@mui/icons-material/Security";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useAuth } from "../context/AuthContext";

// Grouped menu with sections. `perm: null` means visible to every authenticated user.
const SECTIONS = [
  {
    label: "MAIN",
    items: [
      {
        name: "Dashboard",
        to: "/",
        icon: <DashboardIcon />,
        perm: null,
        color: "#4f46e5",
      },
      {
        name: "My Desk",
        to: "/my-desk",
        icon: <DesktopMacIcon />,
        perm: null,
        color: "#0891b2",
      },
    ],
  },
  {
    label: "OPERATIONS",
    items: [
      {
        name: "Clients",
        to: "/clients",
        icon: <PeopleIcon />,
        perm: "clients.view",
        color: "#2563eb",
      },
      {
        name: "Services",
        to: "/services",
        icon: <ListAltIcon />,
        perm: "services.view",
        color: "#7c3aed",
      },
      {
        name: "Jobs",
        to: "/jobs",
        icon: <WorkIcon />,
        perm: "jobs.view",
        color: "#db2777",
      },
      {
        name: "Audit",
        to: "/audit",
        icon: <VerifiedIcon />,
        perm: "audit.view",
        color: "#ea580c",
      },
    ],
  },
  {
    label: "INSIGHTS",
    items: [
      {
        name: "Reports",
        to: "/reports",
        icon: <AssessmentIcon />,
        perm: "reports.view",
        color: "#16a34a",
      },
    ],
  },
  {
    label: "ADMINISTRATION",
    items: [
      {
        name: "Users",
        to: "/user",
        icon: <GroupIcon />,
        perm: "users.view",
        color: "#0ea5e9",
      },
      {
        name: "Roles",
        to: "/roles",
        icon: <GroupWorkIcon />,
        perm: "permissions.manage",
        color: "#8b5cf6",
      },
      {
        name: "Permissions",
        to: "/permissions",
        icon: <SecurityIcon />,
        perm: "permissions.manage",
        color: "#d97706",
      },
      {
        name: "Settings",
        to: "/settings",
        icon: <SettingsIcon />,
        perm: "settings.manage",
        color: "#64748b",
      },
    ],
  },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  // Filter out items user can't access, then remove empty sections
  const visibleSections = SECTIONS.map((sec) => ({
    ...sec,
    items: sec.items.filter((it) => !it.perm || can(it.perm)),
  })).filter((sec) => sec.items.length > 0);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      {/* HEADER / BRAND */}
      <Box
        sx={{
          px: 2.5,
          py: 2.25,
          background:
            "linear-gradient(135deg, #4f46e5 0%, #6d28d9 50%, #9333ea 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            top: -30,
            right: -30,
            width: 110,
            height: 110,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.08)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.22)",
              width: 40,
              height: 40,
              backdropFilter: "blur(8px)",
            }}
          >
            <AccountBalanceIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ lineHeight: 1.1, fontWeight: 800, letterSpacing: 0.2 }}
              noWrap
            >
              CA Portal
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.85, letterSpacing: 0.5 }}
            >
              Practice Manager
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* USER PROFILE CARD */}
      <Box
        sx={{
          m: 1.5,
          p: 1.25,
          borderRadius: 2,
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: "primary.main",
            fontWeight: 800,
            fontSize: 14,
          }}
        >
          {user.name?.[0]?.toUpperCase() || "U"}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {user.name}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mt: 0.25 }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "success.main",
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {user.role}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* MENU */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.25, pb: 1 }}>
        {visibleSections.map((sec) => (
          <Box key={sec.label} sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                px: 1.5,
                pt: 1.25,
                pb: 0.5,
                color: "text.disabled",
                fontWeight: 700,
                fontSize: "0.65rem",
                letterSpacing: 0.8,
              }}
            >
              {sec.label}
            </Typography>
            <List disablePadding>
              {sec.items.map((m) => (
                <ListItemButton
                  key={m.to}
                  component={NavLink}
                  to={m.to}
                  end={m.to === "/"}
                  onClick={onNavigate}
                  sx={{
                    borderRadius: 2,
                    mb: 0.25,
                    py: 0.75,
                    pl: 1.5,
                    position: "relative",
                    color: "text.secondary",
                    transition: "all 0.15s ease",
                    "& .MuiListItemIcon-root": {
                      color: m.color,
                    },
                    "&:hover": {
                      bgcolor: "action.hover",
                      transform: "translateX(2px)",
                    },
                    "&.active": {
                      bgcolor: (t) =>
                        t.palette.mode === "light"
                          ? "rgba(79,70,229,0.10)"
                          : "rgba(139,92,246,0.18)",
                      color: "primary.main",
                      fontWeight: 700,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 8,
                        bottom: 8,
                        width: 3,
                        borderRadius: 3,
                        bgcolor: "primary.main",
                      },
                      "& .MuiListItemText-primary": {
                        fontWeight: 700,
                        color: "primary.main",
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    {React.cloneElement(m.icon, { fontSize: "small" })}
                  </ListItemIcon>
                  <ListItemText
                    primary={m.name}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* FOOTER / LOGOUT */}
      <Divider />
      <Box sx={{ p: 1.25 }}>
        <Tooltip title="Sign out" placement="top">
          <ListItemButton
            onClick={() => {
              logout();
              navigate("/login");
            }}
            sx={{
              borderRadius: 2,
              py: 0.75,
              color: "error.main",
              "&:hover": {
                bgcolor: "error.main",
                color: "#fff",
                "& .MuiListItemIcon-root": { color: "#fff" },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34, color: "error.main" }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: 13, fontWeight: 700 }}
            />
          </ListItemButton>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: "text.disabled",
            mt: 1,
            fontSize: "0.65rem",
          }}
        >
          v1.0 · Kiyunet Technology
        </Typography>
      </Box>
    </Box>
  );
}
